import { useEffect, useRef } from "react";
import { clearToken, getToken, redirectToLogin } from "./api";

/**
 * Deconnexion automatique apres une periode d'inactivite (defaut 1 h).
 *
 * Pourquoi : un onglet back-office laisse ouvert longtemps (ex. repris le
 * lendemain) finit avec un jeton expire (TTL serveur 12 h) -> la 1re requete
 * 401 faisait planter la page. On preempte : au-dela de `idleMs` sans
 * interaction, on purge le jeton et on renvoie proprement vers /login.
 *
 * Robuste a l'onglet en arriere-plan : les timers sont throttles quand l'onglet
 * n'est pas visible, donc on recalcule l'inactivite a partir d'un timestamp au
 * retour de visibilite/focus (cas "repris le lendemain") plutot que de se fier
 * au seul setInterval.
 */
const DEFAULT_IDLE_MS = 60 * 60 * 1000; // 1 h

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
  "click",
];

export function useIdleLogout(idleMs: number = DEFAULT_IDLE_MS): void {
  const lastActivity = useRef<number>(Date.now());

  useEffect(() => {
    const logoutIfIdle = () => {
      if (!getToken()) return; // deja deconnecte
      if (Date.now() - lastActivity.current >= idleMs) {
        clearToken();
        redirectToLogin("idle");
      }
    };

    // Throttle la mise a jour du timestamp (les mousemove sont tres frequents).
    let throttled = false;
    const onActivity = () => {
      if (throttled) return;
      throttled = true;
      lastActivity.current = Date.now();
      window.setTimeout(() => {
        throttled = false;
      }, 1000);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") logoutIfIdle();
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true }),
    );
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // Verification periodique (onglet au premier plan).
    const interval = window.setInterval(logoutIfIdle, 30_000);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(interval);
    };
  }, [idleMs]);
}
